import type { Key } from "@heroui/react";
import {
  Button,
  Card,
  Chip,
  Drawer,
  Form,
  Input,
  Label,
  ListBox,
  NumberField,
  SearchField,
  Select,
  Switch,
  Tabs,
  TextArea,
  TextField,
} from "@heroui/react";
import {
  Banknote,
  Camera,
  Check,
  Download,
  FileText,
  Mic,
  Moon,
  Plus,
  Search,
  Send,
  Settings,
  Sun,
  Trash2,
  WalletCards,
  X,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  answerQuery,
  applyTransactionBalance,
  convert,
  exportLedger,
  filterByWindow,
  filterTimeline,
  generateInsights,
  loadLedger,
  makeTransaction,
  metricsFor,
  parseNaturalLanguage,
  saveLedger,
} from "./ledger";
import { getOpenRouterProxyStatus, parseReceiptWithOpenRouter, parseTextWithOpenRouter } from "./openRouter";
import type {
  Account,
  DashboardWindow,
  LedgerState,
  ParsedTransactionCandidate,
  TimelineFilter,
  TransactionKind,
  TransactionRecord,
  TransactionSource,
} from "./types";

type ActiveSheet =
  | { kind: "quickAdd" }
  | { kind: "record"; record: TransactionRecord }
  | { kind: "accounts" }
  | { kind: "settings" }
  | null;

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

type ThemeMode = "light" | "dark";
type QuickKind = Extract<TransactionKind, "expense" | "income">;

type QuickDraft = {
  kind: QuickKind;
  merchantName: string;
  amountPHP: number;
  category: string;
  accountId: string;
  happenedAtLocal: string;
  note: string;
};

const THEME_STORAGE_KEY = "clearspend.pwa.theme";

const filterLabels: Record<TimelineFilter, string> = {
  all: "全部",
  expense: "支出",
  income: "收入",
  transfer: "转账",
  review: "待复核",
};

const windowLabels: Record<DashboardWindow, string> = {
  today: "今日",
  month: "本月",
  rolling30: "最近30天",
};

const kindLabels: Record<QuickKind, string> = {
  expense: "支出",
  income: "收入",
};

const categories = ["餐饮", "交通", "日用", "医疗健康", "订阅", "房租", "收入", "未分类"];

export default function App() {
  const [ledger, setLedger] = useState<LedgerState>(() => loadLedger());
  const [windowKey, setWindowKey] = useState<DashboardWindow>("month");
  const [filter, setFilter] = useState<TimelineFilter>("all");
  const [input, setInput] = useState("");
  const [queryAnswer, setQueryAnswer] = useState<string | null>(null);
  const [sheet, setSheet] = useState<ActiveSheet>(null);
  const [accountExpanded, setAccountExpanded] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [theme, setTheme] = useState<ThemeMode>(() => loadThemePreference());
  const [quickDraft, setQuickDraft] = useState<QuickDraft>(() => createQuickDraft(ledger.accounts[0]?.id));
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const scoped = useMemo(() => filterByWindow(ledger.transactions, windowKey), [ledger.transactions, windowKey]);
  const metrics = useMemo(() => metricsFor(scoped), [scoped]);
  const monthMetrics = useMemo(() => metricsFor(filterByWindow(ledger.transactions, "month")), [ledger.transactions]);
  const accountBalancePHP = useMemo(() => ledger.accounts.reduce((sum, account) => sum + account.balance, 0), [ledger.accounts]);
  const timeline = useMemo(() => {
    const filtered = filterTimeline(scoped, filter);
    const needle = searchText.trim().toLowerCase();
    if (!needle) return filtered;
    return filtered.filter((record) => {
      const haystack = [
        record.merchantName,
        record.merchantBranch,
        record.note,
        record.accountId,
        record.ownershipTags.join(" "),
        ...record.lineItems.flatMap((item) => [item.nameOriginal, item.nameNormalized, item.category]),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [scoped, filter, searchText]);
  const groupedTimeline = useMemo(() => groupRecordsByDate(timeline.slice(0, 18)), [timeline]);
  const insights = useMemo(() => generateInsights(ledger.transactions, ledger.forecasts), [ledger.transactions, ledger.forecasts]);

  function commit(next: LedgerState) {
    setLedger(next);
    saveLedger(next);
  }

  async function handleSubmit(event?: FormEvent) {
    event?.preventDefault();
    const raw = input.trim();
    if (!raw) return;

    const naturalAnswer = isLikelyQuery(raw) ? answerQuery(raw, ledger.transactions) : null;
    if (naturalAnswer) {
      setQueryAnswer(naturalAnswer);
      setStatus(null);
      setInput("");
      return;
    }

    setStatus("正在理解这笔记录");
    const remoteCandidate = await parseTextWithOpenRouter(raw).catch(() => null);
    const candidate = remoteCandidate ?? parseNaturalLanguage(raw);
    if (!candidate) {
      setStatus("没有识别到金额，请补充金额后再试");
      return;
    }
    addCandidate(candidate, "manualText");
    setInput("");
    setQueryAnswer(null);
    setStatus(candidate.confidence >= 0.8 ? "已自动入账" : "已入账，低自信待复核");
  }

  function addCandidate(candidate: ParsedTransactionCandidate, source: TransactionSource, attachmentName?: string) {
    const primaryAccount = chooseAccount(candidate, ledger.accounts);
    const record = makeTransaction(candidate, primaryAccount.id, source);
    const incoming = attachmentName ? { ...record, attachmentName } : record;
    const duplicate = findDuplicate(ledger.transactions, incoming);
    let transactions = ledger.transactions;
    let accounts = ledger.accounts;

    if (duplicate) {
      transactions = ledger.transactions.map((item) => {
        if (item.id !== duplicate.id) return item;
        return {
          ...item,
          updatedAt: new Date().toISOString(),
          confidence: Math.max(item.confidence, incoming.confidence),
          pendingReview: item.pendingReview && incoming.confidence < 0.8,
          merchantName: item.merchantName === "未识别商户" ? incoming.merchantName : item.merchantName,
          merchantBranch: item.merchantBranch || incoming.merchantBranch,
          note: [item.note, incoming.note].filter(Boolean).join(" / "),
          lineItems: item.lineItems.length >= incoming.lineItems.length ? item.lineItems : incoming.lineItems,
          attachmentName: item.attachmentName || incoming.attachmentName,
        };
      });
    } else {
      transactions = [incoming, ...ledger.transactions];
      accounts = applyTransactionBalance(ledger.accounts, incoming, 1);
    }

    commit({
      ...ledger,
      accounts,
      transactions,
      audits: [
        {
          id: crypto.randomUUID(),
          happenedAt: new Date().toISOString(),
          action: duplicate ? "transaction_merged" : "transaction_created",
          recordId: duplicate?.id ?? incoming.id,
          payload: JSON.stringify({ confidence: incoming.confidence, source }),
        },
        ...ledger.audits,
      ],
    });
  }

  function addQuickTransaction(draft: QuickDraft) {
    const accountId = draft.accountId || ledger.accounts[0]?.id;
    if (!accountId) {
      setStatus("请先创建可用账户");
      return;
    }
    if (!draft.amountPHP || draft.amountPHP <= 0) {
      setStatus("请输入大于 0 的金额");
      return;
    }
    const merchantName = draft.merchantName.trim() || (draft.kind === "income" ? "收入" : "未命名支出");
    const category = draft.category || (draft.kind === "income" ? "收入" : "未分类");
    const record = makeTransaction(
      {
        happenedAt: fromLocalDateTimeInput(draft.happenedAtLocal),
        kind: draft.kind,
        merchantName,
        merchantBranch: "",
        note: draft.note.trim() || merchantName,
        amountOriginal: draft.amountPHP,
        currency: "PHP",
        confidence: 1,
        ownershipTags: ["个人"],
        lineItems: [
          {
            nameOriginal: category,
            nameNormalized: category,
            quantity: 1,
            unit: "笔",
            category,
            priceOriginal: draft.amountPHP,
          },
        ],
      },
      accountId,
      "manualText",
    );

    commit({
      ...ledger,
      accounts: applyTransactionBalance(ledger.accounts, record, 1),
      transactions: [record, ...ledger.transactions],
      audits: [
        {
          id: crypto.randomUUID(),
          happenedAt: new Date().toISOString(),
          action: "transaction_created",
          recordId: record.id,
          payload: JSON.stringify({ source: "quick_form", confidence: record.confidence }),
        },
        ...ledger.audits,
      ],
    });
    setStatus(`${kindLabels[draft.kind]}已保存`);
    setSheet(null);
  }

  function openQuickAdd(kind: QuickKind = "expense") {
    setQuickDraft(createQuickDraft(ledger.accounts[0]?.id, kind));
    setSheet({ kind: "quickAdd" });
  }

  function updateRecord(record: TransactionRecord) {
    const previous = ledger.transactions.find((item) => item.id === record.id);
    if (!previous) return;
    const withoutOld = applyTransactionBalance(ledger.accounts, previous, -1);
    const withNew = applyTransactionBalance(withoutOld, record, 1);
    commit({
      ...ledger,
      accounts: withNew,
      transactions: ledger.transactions.map((item) => (item.id === record.id ? { ...record, updatedAt: new Date().toISOString() } : item)),
    });
    setSheet({ kind: "record", record });
  }

  function softDelete(record: TransactionRecord) {
    const deleted = { ...record, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    commit({
      ...ledger,
      accounts: applyTransactionBalance(ledger.accounts, record, -1),
      transactions: ledger.transactions.map((item) => (item.id === record.id ? deleted : item)),
      audits: [
        {
          id: crypto.randomUUID(),
          happenedAt: new Date().toISOString(),
          action: "soft_deleted",
          recordId: record.id,
          payload: "{}",
        },
        ...ledger.audits,
      ],
    });
    setSheet(null);
  }

  function markReviewed(record: TransactionRecord) {
    updateRecord({ ...record, pendingReview: false, confidence: Math.max(record.confidence, 0.82) });
  }

  function reconcileAccount(accountId: string, nextBalance: number) {
    const account = ledger.accounts.find((item) => item.id === accountId);
    if (!account) return;
    const delta = nextBalance - account.balance;
    if (Math.abs(delta) < 0.01) return;
    const converted = convert(Math.abs(delta), "PHP");
    const adjustment = makeTransaction(
      {
        happenedAt: new Date().toISOString(),
        kind: "adjustment",
        merchantName: "账户余额对齐",
        merchantBranch: "",
        note: `${account.name} 对齐到实际余额`,
        amountOriginal: Math.abs(delta),
        currency: "PHP",
        confidence: 1,
        ownershipTags: ["system"],
        lineItems: [
          {
            nameOriginal: delta >= 0 ? "余额增加" : "余额减少",
            nameNormalized: "余额调整",
            quantity: 1,
            unit: "次",
            category: "adjustment",
            priceOriginal: Math.abs(delta),
          },
        ],
      },
      account.id,
      "system",
    );
    commit({
      ...ledger,
      accounts: ledger.accounts.map((item) =>
        item.id === accountId ? { ...item, balance: nextBalance, updatedAt: new Date().toISOString() } : item,
      ),
      transactions: [{ ...adjustment, amountPHP: Math.abs(delta), amountCNY: converted.cny }, ...ledger.transactions],
    });
  }

  async function handleReceipt(file: File | undefined) {
    if (!file) return;
    setStatus("正在优化图片");
    const dataUrl = await compressImage(file);
    setStatus("正在识别小票");
    const remoteCandidate = await parseReceiptWithOpenRouter(dataUrl).catch(() => null);
    const fallback = parseNaturalLanguage(`${file.name} 小票 0 PHP`);

    if (remoteCandidate) {
      addCandidate(remoteCandidate, "receipt", file.name);
      setStatus(remoteCandidate.confidence >= 0.8 ? "小票已自动入账" : "小票已入账，待复核");
      return;
    }

    if (fallback) {
      addCandidate({ ...fallback, amountOriginal: 0, confidence: 0.35, merchantName: "待识别小票", note: file.name }, "receipt", file.name);
    }
    setStatus("已保存小票入口；配置云端 AI Key 后可自动识别");
  }

  function toggleVoice() {
    const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Speech) {
      setStatus("当前浏览器不支持语音识别");
      return;
    }

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }

    const recognition = new Speech();
    recognition.lang = "zh-CN";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((result) => result[0]?.transcript ?? "")
        .join("");
      setInput(transcript);
    };
    recognition.onend = () => setIsRecording(false);
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(true);
  }

  async function installApp() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    setInstallPrompt(null);
  }

  return (
    <main className="finance-app">
      <section className="mobile-shell">
        <header className="app-topbar">
          <Button isIconOnly aria-label="搜索" variant="tertiary" onPress={() => setSearchMode((value) => !value)}>
            <Search size={18} />
          </Button>
          <div className="brand-lockup">
            <span>ClearSpend</span>
            <small>PWA 本地账本</small>
          </div>
          <div className="topbar-actions">
            <Button isIconOnly aria-label="快速记账" variant="secondary" onPress={() => openQuickAdd("expense")}>
              <Plus size={18} />
            </Button>
            <Button isIconOnly aria-label="设置" variant="tertiary" onPress={() => setSheet({ kind: "settings" })}>
              <Settings size={18} />
            </Button>
          </div>
        </header>

        {searchMode ? (
          <SearchField className="search-panel" fullWidth value={searchText} onChange={setSearchText}>
            <Label className="sr-only">搜索交易</Label>
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="搜索交易、商品、商户、账户" />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
        ) : null}

        <SummaryPanel
          accountBalancePHP={accountBalancePHP}
          metrics={metrics}
          monthMetrics={monthMetrics}
          windowKey={windowKey}
          setWindowKey={setWindowKey}
        />

        <InsightStrip insights={insights} />

        {queryAnswer ? (
          <Card className="answer-card" variant="secondary">
            <Card.Content className="answer-content">
              <span>{queryAnswer}</span>
              <Button isIconOnly aria-label="关闭查询结果" size="sm" variant="tertiary" onPress={() => setQueryAnswer(null)}>
                <X size={14} />
              </Button>
            </Card.Content>
          </Card>
        ) : null}

        <Form className={isRecording ? "ai-composer recording" : "ai-composer"} onSubmit={handleSubmit}>
          <TextField className="composer-field" value={input} onChange={setInput}>
            <Label className="sr-only">自然语言记账</Label>
            <TextArea placeholder="说：楼下711买水花了3块" rows={1} />
          </TextField>
          <Button isIconOnly aria-label="语音输入" type="button" variant={isRecording ? "danger" : "tertiary"} onPress={toggleVoice}>
            <Mic size={18} />
          </Button>
          <Button isIconOnly aria-label="小票图片" type="button" variant="tertiary" onPress={() => fileInputRef.current?.click()}>
            <Camera size={18} />
          </Button>
          <Button isIconOnly aria-label="提交" type="submit">
            <Send size={17} />
          </Button>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(event) => void handleReceipt(event.currentTarget.files?.[0])}
          />
        </Form>

        {status ? <div className="status-line">{status}</div> : null}

        <section className="accounts-section">
          <Button className="section-trigger" fullWidth variant="tertiary" onPress={() => setAccountExpanded((value) => !value)}>
            <span>
              <WalletCards size={17} />
              账户余额
            </span>
            <span>{accountExpanded ? "收起" : "展开"}</span>
          </Button>
          {accountExpanded ? (
            <div className="account-grid">
              {ledger.accounts.map((account) => (
                <Card key={account.id} className="account-card" role="button" tabIndex={0} variant="default" onClick={() => setSheet({ kind: "accounts" })}>
                  <Card.Content>
                    <span>{account.name}</span>
                    <strong>{formatPHP(account.balance)}</strong>
                  </Card.Content>
                </Card>
              ))}
            </div>
          ) : null}
        </section>

        <Tabs
          className="filter-tabs"
          selectedKey={filter}
          variant="secondary"
          onSelectionChange={(key) => setFilter(key as TimelineFilter)}
        >
          <Tabs.ListContainer>
            <Tabs.List aria-label="交易筛选">
              {(Object.keys(filterLabels) as TimelineFilter[]).map((key) => (
                <Tabs.Tab key={key} id={key}>
                  {filterLabels[key]}
                  <Tabs.Indicator />
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs.ListContainer>
        </Tabs>

        <section className="timeline-section">
          {timeline.length === 0 ? (
            <EmptyState setInput={setInput} openQuickAdd={openQuickAdd} />
          ) : (
            groupedTimeline.map((group) => (
              <div key={group.label} className="date-group">
                <div className="date-label">{group.label}</div>
                {group.records.map((record) => (
                  <TimelineRow
                    key={record.id}
                    record={record}
                    account={ledger.accounts.find((account) => account.id === record.accountId)}
                    onOpen={() => setSheet({ kind: "record", record })}
                    onDelete={() => softDelete(record)}
                    onReview={() => markReviewed(record)}
                  />
                ))}
              </div>
            ))
          )}
        </section>

        <footer className="footer-actions">
          <Button fullWidth variant="secondary" onPress={() => setSearchMode(true)}>
            <FileText size={16} />
            查看全部记录
          </Button>
          {installPrompt ? (
            <Button fullWidth variant="secondary" onPress={() => void installApp()}>
              <Download size={16} />
              安装
            </Button>
          ) : null}
        </footer>
      </section>

      <QuickAddDrawer
        accounts={ledger.accounts}
        draft={quickDraft}
        isOpen={sheet?.kind === "quickAdd"}
        setDraft={setQuickDraft}
        onClose={() => setSheet(null)}
        onSave={addQuickTransaction}
      />
      {sheet?.kind === "record" ? (
        <RecordDrawer
          record={sheet.record}
          accounts={ledger.accounts}
          onClose={() => setSheet(null)}
          onSave={updateRecord}
          onDelete={softDelete}
          onReviewed={markReviewed}
        />
      ) : null}
      <AccountsDrawer
        accounts={ledger.accounts}
        isOpen={sheet?.kind === "accounts"}
        onClose={() => setSheet(null)}
        onReconcile={reconcileAccount}
      />
      <SettingsDrawer
        ledger={ledger}
        installAvailable={Boolean(installPrompt)}
        isOpen={sheet?.kind === "settings"}
        theme={theme}
        onClose={() => setSheet(null)}
        onInstall={() => void installApp()}
        onThemeChange={setTheme}
        onUpdate={(next) => commit(next)}
      />
    </main>
  );
}

function SummaryPanel({
  accountBalancePHP,
  metrics,
  monthMetrics,
  windowKey,
  setWindowKey,
}: {
  accountBalancePHP: number;
  metrics: ReturnType<typeof metricsFor>;
  monthMetrics: ReturnType<typeof metricsFor>;
  windowKey: DashboardWindow;
  setWindowKey: (value: DashboardWindow) => void;
}) {
  return (
    <section className="summary-panel">
      <Tabs
        className="window-tabs"
        selectedKey={windowKey}
        variant="secondary"
        onSelectionChange={(key) => setWindowKey(key as DashboardWindow)}
      >
        <Tabs.ListContainer>
          <Tabs.List aria-label="统计范围">
            {(Object.keys(windowLabels) as DashboardWindow[]).map((key) => (
              <Tabs.Tab key={key} id={key}>
                {windowLabels[key]}
                <Tabs.Indicator />
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.ListContainer>
      </Tabs>

      <Card className="balance-card" variant="default">
        <Card.Header>
          <Card.Description>余额</Card.Description>
          <Card.Title className={metrics.netPHP < 0 ? "amount-title negative" : "amount-title"}>
            {formatPHP(accountBalancePHP)}
          </Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="sparkline-wrap">
            <Sparkline negative={metrics.netPHP < 0} />
            <span>当前范围净额 {formatPHP(metrics.netPHP)}</span>
          </div>
        </Card.Content>
      </Card>

      <div className="metric-grid">
        <MetricCard label="本月支出" value={monthMetrics.expensePHP} tone="danger" />
        <MetricCard label="本月收入" value={monthMetrics.incomePHP} tone="success" />
      </div>
    </section>
  );
}

function MetricCard({ label, value, tone }: { label: string; value: number; tone: "success" | "danger" }) {
  return (
    <Card className="metric-card" variant="secondary">
      <Card.Content>
        <span>{label}</span>
        <strong className={tone}>{formatPHP(value)}</strong>
      </Card.Content>
    </Card>
  );
}

function Sparkline({ negative }: { negative: boolean }) {
  const points = negative ? "0,18 18,14 36,22 54,12 72,24 90,20 108,28 126,30" : "0,28 18,24 36,26 54,18 72,16 90,10 108,12 126,7";
  return (
    <svg className="sparkline" viewBox="0 0 126 36" aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function InsightStrip({ insights }: { insights: string[] }) {
  if (insights.length === 0) return null;
  return (
    <section className="insights">
      {insights.slice(0, 3).map((insight, index) => (
        <Chip key={insight} className="insight-chip" color={index === 0 ? "warning" : "default"} variant="soft">
          <Chip.Label>{insight}</Chip.Label>
        </Chip>
      ))}
    </section>
  );
}

function TimelineRow({
  record,
  account,
  onOpen,
  onDelete,
  onReview,
}: {
  record: TransactionRecord;
  account?: Account;
  onOpen: () => void;
  onDelete: () => void;
  onReview: () => void;
}) {
  const signedAmount = isPositiveRecord(record) ? record.amountPHP : -record.amountPHP;
  const category = record.lineItems[0]?.category ?? "uncategorized";
  return (
    <Card className="transaction-card" role="button" tabIndex={0} variant="default" onClick={onOpen} onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") onOpen();
    }}>
      <Card.Content>
        <div className="transaction-main">
          <div className="transaction-icon">
            <Banknote size={17} />
          </div>
          <div className="transaction-copy">
            <div className="transaction-title">
              {record.pendingReview ? <span className="review-dot" /> : null}
              <strong>{record.merchantName}</strong>
              {record.merchantBranch ? <span>{record.merchantBranch}</span> : null}
            </div>
            <div className="transaction-meta">
              <Chip color={categoryColor(category)} size="sm" variant="soft">
                <Chip.Label>{category}</Chip.Label>
              </Chip>
              <span>{account?.name ?? "未分配"}</span>
              <span>{record.ownershipTags.join(" / ")}</span>
            </div>
          </div>
        </div>
        <div className="transaction-side">
          <strong className={isPositiveRecord(record) ? "income" : "expense"}>{formatPHP(signedAmount)}</strong>
          <span>¥{record.amountCNY.toFixed(2)}</span>
          <div className="row-actions" onClick={(event) => event.stopPropagation()}>
            <Button isIconOnly aria-label={record.pendingReview ? "完成复核" : "编辑"} size="sm" variant="tertiary" onPress={record.pendingReview ? onReview : onOpen}>
              {record.pendingReview ? <Check size={15} /> : <FileText size={15} />}
            </Button>
            <Button isIconOnly aria-label="删除" size="sm" variant="danger" onPress={onDelete}>
              <Trash2 size={15} />
            </Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}

function EmptyState({ setInput, openQuickAdd }: { setInput: (value: string) => void; openQuickAdd: (kind?: QuickKind) => void }) {
  const examples = ["刚刚在楼下711买了一瓶水，花了3块", "收到 Airbnb 18000 PHP 入账预告", "用 GCash 给宠物买药花了450比索"];
  return (
    <Card className="empty-state" variant="secondary">
      <Card.Header>
        <Card.Title>还没有记录</Card.Title>
        <Card.Description>可以用一句话记账，也可以打开快速表单。</Card.Description>
      </Card.Header>
      <Card.Content>
        <Button fullWidth onPress={() => openQuickAdd("expense")}>
          <Plus size={16} />
          新增支出
        </Button>
        <div className="example-list">
          {examples.map((example) => (
            <Button key={example} fullWidth variant="tertiary" onPress={() => setInput(example)}>
              {example}
            </Button>
          ))}
        </div>
      </Card.Content>
    </Card>
  );
}

function QuickAddDrawer({
  accounts,
  draft,
  isOpen,
  setDraft,
  onClose,
  onSave,
}: {
  accounts: Account[];
  draft: QuickDraft;
  isOpen: boolean;
  setDraft: (draft: QuickDraft) => void;
  onClose: () => void;
  onSave: (draft: QuickDraft) => void;
}) {
  return (
    <Drawer.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content placement="bottom">
        <Drawer.Dialog className="app-drawer">
          <Drawer.Handle />
          <Drawer.CloseTrigger aria-label="关闭" />
          <Drawer.Header>
            <Drawer.Heading>快速记账</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body>
            <Form className="drawer-form" onSubmit={(event) => {
              event.preventDefault();
              onSave(draft);
            }}>
              <Select
                fullWidth
                placeholder="选择类型"
                value={draft.kind}
                onChange={(value) => {
                  const kind = String(value ?? "expense") as QuickKind;
                  setDraft({ ...draft, kind, category: kind === "income" ? "收入" : "餐饮" });
                }}
              >
                <Label>类型</Label>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {(Object.keys(kindLabels) as QuickKind[]).map((kind) => (
                      <ListBox.Item key={kind} id={kind} textValue={kindLabels[kind]}>
                        {kindLabels[kind]}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              <TextField fullWidth value={draft.merchantName} onChange={(merchantName) => setDraft({ ...draft, merchantName })}>
                <Label>商户 / 来源</Label>
                <Input placeholder={draft.kind === "income" ? "Airbnb / 工资" : "7-Eleven / GCash"} />
              </TextField>

              <NumberField fullWidth minValue={0} value={draft.amountPHP} onChange={(amountPHP) => setDraft({ ...draft, amountPHP })}>
                <Label>金额 PHP</Label>
                <NumberField.Group>
                  <NumberField.Input />
                </NumberField.Group>
              </NumberField>

              <Select fullWidth placeholder="选择分类" value={draft.category} onChange={(value) => setDraft({ ...draft, category: String(value ?? "未分类") })}>
                <Label>分类</Label>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {categories.map((category) => (
                      <ListBox.Item key={category} id={category} textValue={category}>
                        {category}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              <Select fullWidth placeholder="选择账户" value={draft.accountId} onChange={(value) => setDraft({ ...draft, accountId: String(value ?? "") })}>
                <Label>账户</Label>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {accounts.map((account) => (
                      <ListBox.Item key={account.id} id={account.id} textValue={account.name}>
                        {account.name}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              <TextField fullWidth value={draft.happenedAtLocal} onChange={(happenedAtLocal) => setDraft({ ...draft, happenedAtLocal })}>
                <Label>日期</Label>
                <Input type="datetime-local" />
              </TextField>

              <TextField fullWidth value={draft.note} onChange={(note) => setDraft({ ...draft, note })}>
                <Label>备注</Label>
                <TextArea placeholder="可选" rows={3} />
              </TextField>

              <Drawer.Footer className="drawer-actions">
                <Button fullWidth type="button" variant="tertiary" onPress={onClose}>
                  取消
                </Button>
                <Button fullWidth type="submit">
                  保存
                </Button>
              </Drawer.Footer>
            </Form>
          </Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

function RecordDrawer({
  record,
  accounts,
  onClose,
  onSave,
  onDelete,
  onReviewed,
}: {
  record: TransactionRecord;
  accounts: Account[];
  onClose: () => void;
  onSave: (record: TransactionRecord) => void;
  onDelete: (record: TransactionRecord) => void;
  onReviewed: (record: TransactionRecord) => void;
}) {
  const [draft, setDraft] = useState(record);
  const showItems = draft.pendingReview || draft.lineItems.length > 1;

  return (
    <Drawer.Backdrop isOpen onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content placement="bottom">
        <Drawer.Dialog className="app-drawer">
          <Drawer.Handle />
          <Drawer.CloseTrigger aria-label="关闭" />
          <Drawer.Header>
            <Drawer.Heading>交易详情</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body>
            <Form className="drawer-form" onSubmit={(event) => {
              event.preventDefault();
              onSave(draft);
            }}>
              <TextField fullWidth value={draft.merchantName} onChange={(merchantName) => setDraft({ ...draft, merchantName })}>
                <Label>商户</Label>
                <Input />
              </TextField>

              <NumberField fullWidth minValue={0} value={draft.amountPHP} onChange={(amountPHP) => {
                setDraft({
                  ...draft,
                  amountPHP,
                  amountCNY: convert(amountPHP, "PHP").cny,
                  amountOriginal: draft.currencyOriginal === "PHP" ? amountPHP : draft.amountOriginal,
                });
              }}>
                <Label>金额 PHP</Label>
                <NumberField.Group>
                  <NumberField.Input />
                </NumberField.Group>
              </NumberField>

              <Select fullWidth value={draft.accountId} onChange={(value) => setDraft({ ...draft, accountId: String(value ?? draft.accountId) })}>
                <Label>账户</Label>
                <Select.Trigger>
                  <Select.Value />
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {accounts.map((account) => (
                      <ListBox.Item key={account.id} id={account.id} textValue={account.name}>
                        {account.name}
                        <ListBox.ItemIndicator />
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>

              <TextField fullWidth value={toLocalDateTimeInput(draft.happenedAt)} onChange={(value) => setDraft({ ...draft, happenedAt: fromLocalDateTimeInput(value) })}>
                <Label>日期</Label>
                <Input type="datetime-local" />
              </TextField>

              <TextField fullWidth value={draft.note} onChange={(note) => setDraft({ ...draft, note })}>
                <Label>备注</Label>
                <TextArea rows={3} />
              </TextField>

              {showItems ? (
                <div className="line-items">
                  <div className="drawer-subtitle">商品明细</div>
                  {draft.lineItems.map((item) => (
                    <div key={item.id} className="line-item">
                      <div>
                        <span>{item.nameOriginal}</span>
                        <small>
                          {item.nameNormalized} / {item.category}
                        </small>
                      </div>
                      <strong>{formatPHP(item.pricePHP)}</strong>
                    </div>
                  ))}
                </div>
              ) : null}

              <Drawer.Footer className="drawer-actions">
                {draft.pendingReview ? (
                  <Button fullWidth type="button" variant="secondary" onPress={() => onReviewed(draft)}>
                    完成复核
                  </Button>
                ) : null}
                <Button fullWidth type="button" variant="danger" onPress={() => onDelete(draft)}>
                  删除
                </Button>
                <Button fullWidth type="submit">
                  保存
                </Button>
              </Drawer.Footer>
            </Form>
          </Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

function AccountsDrawer({
  accounts,
  isOpen,
  onClose,
  onReconcile,
}: {
  accounts: Account[];
  isOpen: boolean;
  onClose: () => void;
  onReconcile: (accountId: string, balance: number) => void;
}) {
  const [drafts, setDrafts] = useState(() => Object.fromEntries(accounts.map((account) => [account.id, account.balance])));
  useEffect(() => {
    if (isOpen) setDrafts(Object.fromEntries(accounts.map((account) => [account.id, account.balance])));
  }, [accounts, isOpen]);

  return (
    <Drawer.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content placement="bottom">
        <Drawer.Dialog className="app-drawer">
          <Drawer.Handle />
          <Drawer.CloseTrigger aria-label="关闭" />
          <Drawer.Header>
            <Drawer.Heading>账户余额</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body>
            <div className="account-editor">
              {accounts.map((account) => (
                <Card key={account.id} className="account-editor-card" variant="secondary">
                  <Card.Content>
                    <div>
                      <strong>{account.name}</strong>
                      <span>{account.kind}</span>
                    </div>
                    <NumberField value={drafts[account.id] ?? 0} onChange={(value) => setDrafts({ ...drafts, [account.id]: value })}>
                      <Label className="sr-only">{account.name} 余额</Label>
                      <NumberField.Group>
                        <NumberField.Input />
                      </NumberField.Group>
                    </NumberField>
                    <Button variant="secondary" onPress={() => onReconcile(account.id, drafts[account.id] ?? 0)}>
                      对齐
                    </Button>
                  </Card.Content>
                </Card>
              ))}
            </div>
          </Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

function SettingsDrawer({
  ledger,
  installAvailable,
  isOpen,
  theme,
  onClose,
  onInstall,
  onThemeChange,
  onUpdate,
}: {
  ledger: LedgerState;
  installAvailable: boolean;
  isOpen: boolean;
  theme: ThemeMode;
  onClose: () => void;
  onInstall: () => void;
  onThemeChange: (theme: ThemeMode) => void;
  onUpdate: (state: LedgerState) => void;
}) {
  const [aiStatus, setAiStatus] = useState<"checking" | "configured" | "missing">("checking");
  const exportUrl = exportLedger(ledger);

  useEffect(() => {
    let active = true;
    if (!isOpen) return undefined;
    setAiStatus("checking");
    getOpenRouterProxyStatus()
      .then((status) => {
        if (active) setAiStatus(status.configured ? "configured" : "missing");
      })
      .catch(() => {
        if (active) setAiStatus("missing");
      });
    return () => {
      active = false;
    };
  }, [isOpen]);

  return (
    <Drawer.Backdrop isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content placement="bottom">
        <Drawer.Dialog className="app-drawer">
          <Drawer.Handle />
          <Drawer.CloseTrigger aria-label="关闭" />
          <Drawer.Header>
            <Drawer.Heading>设置</Drawer.Heading>
          </Drawer.Header>
          <Drawer.Body>
            <div className="settings-list">
              <Card variant="secondary">
                <Card.Header>
                  <Card.Title>云端 AI 代理</Card.Title>
                  <Card.Description>
                    {aiStatus === "checking"
                      ? "正在检查 OpenRouter 云端配置。"
                      : aiStatus === "configured"
                        ? "已启用。Key 保存在 Cloudflare Secret，不会保存在这台设备。"
                        : "未配置。写入 Cloudflare Secret 后，所有设备都会共用同一个安全代理。"}
                  </Card.Description>
                </Card.Header>
              </Card>

              <Switch isSelected={theme === "dark"} onChange={(selected) => onThemeChange(selected ? "dark" : "light")}>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <Switch.Content>
                  <Label className="theme-switch-label">
                    {theme === "dark" ? <Moon size={16} /> : <Sun size={16} />}
                    深色模式
                  </Label>
                </Switch.Content>
              </Switch>

              <a className="export-link" href={exportUrl} download="clearspend-ledger.json">
                导出账本 JSON
              </a>
              {installAvailable ? (
                <Button fullWidth variant="secondary" onPress={onInstall}>
                  安装到主屏幕
                </Button>
              ) : null}
              <Button fullWidth variant="secondary" onPress={() => onUpdate({ ...ledger, transactions: ledger.transactions.filter((item) => !item.deletedAt) })}>
                隐藏已删除记录
              </Button>
            </div>
          </Drawer.Body>
        </Drawer.Dialog>
      </Drawer.Content>
    </Drawer.Backdrop>
  );
}

function createQuickDraft(accountId = "", kind: QuickKind = "expense"): QuickDraft {
  return {
    kind,
    merchantName: "",
    amountPHP: 0,
    category: kind === "income" ? "收入" : "餐饮",
    accountId,
    happenedAtLocal: toLocalDateTimeInput(new Date().toISOString()),
    note: "",
  };
}

function chooseAccount(candidate: ParsedTransactionCandidate, accounts: Account[]) {
  const note = `${candidate.note} ${candidate.merchantName}`.toLowerCase();
  if (/gcash/.test(note)) return accounts.find((account) => account.name === "GCash") ?? accounts[0];
  if (/binance|usdt|coins\.ph/.test(note)) return accounts.find((account) => account.name === "Binance") ?? accounts[0];
  if (/bpi|debit/.test(note)) return accounts.find((account) => account.name === "BPI Debit") ?? accounts[0];
  if (/cash|现金/.test(note)) return accounts.find((account) => account.name === "随身现金") ?? accounts[0];
  return accounts.find((account) => account.name === "随身现金") ?? accounts[0];
}

function findDuplicate(transactions: TransactionRecord[], incoming: TransactionRecord) {
  return transactions.find((record) => {
    if (record.deletedAt || record.kind !== incoming.kind) return false;
    const timeGap = Math.abs(new Date(record.happenedAt).getTime() - new Date(incoming.happenedAt).getTime());
    const amountGap = Math.abs(record.amountPHP - incoming.amountPHP);
    const merchantMatch = record.merchantName === incoming.merchantName && record.merchantName !== "未识别商户";
    return timeGap <= 1000 * 60 * 5 && amountGap <= 1 && merchantMatch;
  });
}

function isLikelyQuery(text: string) {
  const querySignals = /多少|多少钱|一共|总共|统计|查询|查一下|上个月|最近30天|趋势|改善|how much|spent/i.test(text);
  const recordSignals = /刚刚|买了|花了|收到|转账|付了|消费了|上传|小票|入账/i.test(text);
  if (!querySignals) return false;
  return !recordSignals || /多少|多少钱|一共|总共|统计|查询|查一下|上个月|最近30天|趋势|改善/i.test(text);
}

async function compressImage(file: File) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext("2d");
  ctx?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.68);
}

function formatPHP(amount: number) {
  const abs = Math.abs(amount);
  if (abs >= 100000) return `PHP ${(amount / 1000).toFixed(1)}K`;
  return `PHP ${new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)}`;
}

function isPositiveRecord(record: TransactionRecord) {
  return record.kind === "income" || record.kind === "refund" || record.kind === "transfer";
}

function categoryColor(category: string): "default" | "accent" | "success" | "warning" | "danger" {
  if (/收入|income|refund/i.test(category)) return "success";
  if (/医疗|health|药/i.test(category)) return "danger";
  if (/订阅|subscription|AI/i.test(category)) return "accent";
  if (/餐饮|food|咖啡/i.test(category)) return "warning";
  return "default";
}

function groupRecordsByDate(records: TransactionRecord[]) {
  const groups = new Map<string, TransactionRecord[]>();
  records.forEach((record) => {
    const label = dateGroupLabel(record.happenedAt);
    groups.set(label, [...(groups.get(label) ?? []), record]);
  });
  return Array.from(groups, ([label, groupedRecords]) => ({ label, records: groupedRecords }));
}

function dateGroupLabel(iso: string) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "今天";
  if (date.toDateString() === yesterday.toDateString()) return "昨天";
  return date.toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit", weekday: "short" });
}

function toLocalDateTimeInput(iso: string) {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function fromLocalDateTimeInput(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
}

function loadThemePreference(): ThemeMode {
  const stored = typeof localStorage === "undefined" ? null : localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}
